import React, { createContext, useContext, useState, useEffect } from "react";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

const parseJwt = (token) => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`)
        .join(""),
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    return null;
  }
};

const isTokenExpired = (token) => {
  const decoded = parseJwt(token);
  if (!decoded || !decoded.exp) return true;
  return decoded.exp * 1000 <= Date.now();
};

const clearAuthData = () => {
  localStorage.removeItem("adminToken");
  localStorage.removeItem("token");
  localStorage.removeItem("adminUser");
  localStorage.removeItem("user");
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = () => {
      try {
        const token =
          localStorage.getItem("adminToken") || localStorage.getItem("token");
        const userData =
          localStorage.getItem("adminUser") || localStorage.getItem("user");

        console.log("AuthContext: Checking localStorage", {
          token: !!token,
          userData: !!userData,
        });
        console.log("AuthContext: Raw localStorage data", { token, userData });

        if (token && userData) {
          if (isTokenExpired(token)) {
            console.log("AuthContext: Token expired, clearing auth data");
            clearAuthData();
            setUser(null);
          } else {
            const parsedUser = JSON.parse(userData);
            console.log("AuthContext: User found", parsedUser);
            setUser(parsedUser);
          }
        } else {
          console.log("AuthContext: No auth data found");
          setUser(null);
        }
      } catch (error) {
        console.error("AuthContext: Error initializing auth:", error);
        console.log("AuthContext: Clearing corrupted data");
        clearAuthData();
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const login = (userData, token) => {
    console.log("AuthContext: Login called with", userData);
    setUser(userData);
    if (token) {
      localStorage.setItem("adminToken", token);
      localStorage.setItem("token", token);
      localStorage.setItem("adminUser", JSON.stringify(userData));
      localStorage.setItem("user", JSON.stringify(userData));
    }
  };

  const logout = () => {
    console.log("AuthContext: Logout called");
    setUser(null);
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminUser");
    // Also clear user tokens if they exist to avoid conflicts
    localStorage.removeItem("token");
    localStorage.removeItem("user");
  };

  // Add debugging for localStorage changes
  useEffect(() => {
    const handleStorageChange = (e) => {
      console.log("AuthContext: Storage changed", e.key, e.newValue);
      if (
        e.key === "adminToken" ||
        e.key === "adminUser" ||
        e.key === "token" ||
        e.key === "user"
      ) {
        if (!e.newValue) {
          console.log("AuthContext: Auth data was cleared");
          setUser(null);
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const value = {
    user,
    login,
    logout,
    isAuthenticated: !!user,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
